// src/sections/gamification/plays/uiKit.jsx
// The plays UI language, anchored to the approved bottle-screen reference
// (owner 2026-07-06): deep black/purple stage, restrained glow (rim light on
// the HERO only), clean modern uppercase type with strong hierarchy, subtle
// neon capsules for status, champagne gold reserved for collect/bonus
// moments. Hero objects are IMAGE SLOTS — drop a rendered PNG into
// src/assets/plays/ (see HERO_ASSETS) and it replaces the SVG fallback.

import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';

// ---- palette (tight; no strays) ----
export const NIGHT = {
  bg0: '#08060f',
  bg1: '#140e24',
  violet: '#8b5cf6',
  violetSoft: '#a78bfa',
  cyan: '#22d3ee',
  white: '#f5f3ff',
  dim: 'rgba(245,243,255,0.62)',
  gold: '#f5c644',
  goldHi: '#ffe9a3'
};

// ---- rendered hero assets (drop-in): src/assets/plays/<name>.png ----
// Vite glob: present files win over SVG fallbacks automatically.
const assetModules = import.meta.glob('../../../assets/plays/*.{png,webp}', { eager: true, query: '?url', import: 'default' });
export function heroAssetUrl(name) {
  for (const [path, url] of Object.entries(assetModules)) {
    if (path.endsWith(`/${name}.png`) || path.endsWith(`/${name}.webp`)) return url;
  }
  return null;
}

// Hero slot: rendered image if provided, else the SVG fallback node.
export function Hero({ asset, fallback, size = 260, glow = 'rgba(139,92,246,0.35)', sx }) {
  const url = heroAssetUrl(asset);
  if (url) {
    return (
      <Box
        component="img"
        src={url}
        alt=""
        sx={{
          width: size,
          height: 'auto',
          display: 'block',
          filter: `drop-shadow(0 18px 40px rgba(0,0,0,0.6)) drop-shadow(0 0 34px ${glow})`,
          ...sx
        }}
      />
    );
  }
  return <Box sx={{ display: 'inline-flex', filter: `drop-shadow(0 0 26px ${glow})`, ...sx }}>{fallback}</Box>;
}
Hero.propTypes = {
  asset: PropTypes.string.isRequired,
  fallback: PropTypes.node,
  size: PropTypes.number,
  glow: PropTypes.string,
  sx: PropTypes.object
};

// ---- atmosphere: drifting dust motes + soft vertical light streaks ----
export function Atmosphere() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    let W = 0;
    let H = 0;
    let motes = [];
    // (Re)size the buffer to the element — a stale buffer gets stretched by
    // the browser on viewport/orientation change and smears motes into lines.
    const fit = () => {
      W = canvas.clientWidth;
      H = canvas.clientHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      motes = [...Array(26)].map(() => ({
        x: Math.random() * W,
        y: Math.random() * H,
        r: 0.6 + Math.random() * 1.8,
        vy: -(0.08 + Math.random() * 0.22),
        vx: (Math.random() - 0.5) * 0.08,
        a: 0.1 + Math.random() * 0.4,
        hue: Math.random() > 0.5 ? '167,139,250' : '34,211,238'
      }));
    };
    fit();
    const ro = new ResizeObserver(() => fit());
    ro.observe(canvas);
    let raf;
    let t = 0;
    const tick = () => {
      t += 1;
      ctx.clearRect(0, 0, W, H);
      for (const m of motes) {
        m.y += m.vy;
        m.x += m.vx;
        if (m.y < -4) {
          m.y = H + 4;
          m.x = Math.random() * W;
        }
        const tw = m.a * (0.7 + 0.3 * Math.sin(t * 0.03 + m.x));
        ctx.beginPath();
        ctx.arc(m.x, m.y, m.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${m.hue},${tw})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);
  return (
    <>
      {/* soft vertical light streaks behind the hero (reference: cyan/violet
          columns rising behind the bottle) */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `
            radial-gradient(140px 420px at 38% 62%, rgba(139,92,246,0.16) 0%, transparent 70%),
            radial-gradient(120px 380px at 63% 60%, rgba(34,211,238,0.12) 0%, transparent 70%),
            radial-gradient(400px 240px at 50% 100%, rgba(139,92,246,0.12) 0%, transparent 70%)
          `
        }}
      />
      <Box component="canvas" ref={ref} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
    </>
  );
}

// The stage every screen sits on.
export const nightStage = `
  radial-gradient(560px 300px at 50% -6%, rgba(139,92,246,0.18) 0%, transparent 62%),
  linear-gradient(180deg, #120c20 0%, ${NIGHT.bg0} 60%, #050409 100%)
`;

// ---- top status bar: subtle neon capsules (reference: ••• | token | 0 HITS) ----
export function StatusPill({ children, accent = 'rgba(167,139,250,0.45)', sx }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 2,
        py: 0.6,
        borderRadius: 99,
        bgcolor: 'rgba(16,11,30,0.72)',
        border: `1px solid ${accent}`,
        boxShadow: `0 0 12px ${accent.replace('0.45', '0.18')}`,
        backdropFilter: 'blur(6px)',
        ...sx
      }}
    >
      {children}
    </Box>
  );
}
StatusPill.propTypes = { children: PropTypes.node, accent: PropTypes.string, sx: PropTypes.object };

export function TopBar({ left, center, right }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 'calc(env(safe-area-inset-top, 0px) + 14px)',
        left: 16,
        right: 16,
        display: 'flex',
        alignItems: 'center',
        zIndex: 6
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>{left}</Box>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>{center}</Box>
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>{right}</Box>
    </Box>
  );
}
TopBar.propTypes = { left: PropTypes.node, center: PropTypes.node, right: PropTypes.node };

// ---- headline: two-line uppercase treatment from the reference ----
export function Headline({ kicker, title, sx }) {
  return (
    <Box sx={{ textAlign: 'center', ...sx }}>
      {kicker ? (
        <Typography
          sx={{
            fontWeight: 800,
            letterSpacing: '0.42em',
            textIndent: '0.42em',
            fontSize: { xs: 20, sm: 24 },
            color: NIGHT.violetSoft,
            textShadow: '0 0 22px rgba(139,92,246,0.65)',
            lineHeight: 1.15
          }}
        >
          {kicker}
        </Typography>
      ) : null}
      <Typography
        sx={{
          fontWeight: 900,
          letterSpacing: '0.14em',
          textIndent: '0.14em',
          fontSize: { xs: 40, sm: 52 },
          color: NIGHT.white,
          textShadow: '0 0 30px rgba(167,139,250,0.5), 0 2px 18px rgba(0,0,0,0.8)',
          lineHeight: 1.02
        }}
      >
        {title}
      </Typography>
    </Box>
  );
}
Headline.propTypes = { kicker: PropTypes.string, title: PropTypes.string.isRequired, sx: PropTypes.object };

// Instruction line: "HOLD anywhere to shake the bottle" — bold lead word.
export function Instruction({ lead, rest, sx }) {
  return (
    <Typography sx={{ textAlign: 'center', fontSize: 17, color: NIGHT.dim, ...sx }}>
      <b style={{ color: NIGHT.white, fontWeight: 800 }}>{lead}</b> {rest}
    </Typography>
  );
}
Instruction.propTypes = { lead: PropTypes.string.isRequired, rest: PropTypes.string.isRequired, sx: PropTypes.object };

// ---- gold CTA (collect / start): the ONLY place champagne gold lives ----
export function GoldCTA({ children, onClick, disabled, sx }) {
  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      sx={{
        px: 6,
        py: 1.4,
        borderRadius: 99,
        fontSize: 17,
        fontWeight: 900,
        letterSpacing: '0.2em',
        textIndent: '0.2em',
        fontFamily: 'inherit',
        color: '#1d1503',
        background: `linear-gradient(180deg, ${NIGHT.goldHi} 0%, ${NIGHT.gold} 55%, #caa028 100%)`,
        boxShadow: '0 0 26px rgba(245,198,68,0.45), 0 6px 18px rgba(0,0,0,0.5), inset 0 1px 2px rgba(255,255,255,0.7)',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 0 36px rgba(245,198,68,0.6), 0 8px 22px rgba(0,0,0,0.5)' },
        opacity: disabled ? 0.5 : 1,
        ...sx
      }}
    >
      {children}
    </ButtonBase>
  );
}
GoldCTA.propTypes = { children: PropTypes.node, onClick: PropTypes.func, disabled: PropTypes.bool, sx: PropTypes.object };

// ---- hub section shell: the dark night card every gamification section on
// the Missions hub sits in — hero render left, uppercase letterspaced title,
// dim subtitle, optional action on the right ----
export function NightSection({ art, artSize = 64, artGlow = 'rgba(139,92,246,0.35)', title, subtitle, action, children, sx }) {
  return (
    <Box
      sx={{
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
        background: `
          radial-gradient(420px 200px at 12% 0%, rgba(139,92,246,0.16) 0%, transparent 70%),
          linear-gradient(180deg, #171028 0%, #0c081a 100%)
        `,
        border: '1px solid rgba(167,139,250,0.28)',
        boxShadow: '0 10px 26px rgba(0,0,0,0.35)',
        color: NIGHT.white,
        ...sx
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, rowGap: 1, mb: subtitle || children ? 0.75 : 0 }}>
        {art ? <Hero asset={art} size={artSize} glow={artGlow} sx={{ flexShrink: 0 }} /> : null}
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: 16,
              letterSpacing: '0.12em',
              color: NIGHT.white,
              textShadow: '0 0 18px rgba(167,139,250,0.35)'
            }}
          >
            {String(title || '').toUpperCase()}
          </Typography>
          {subtitle ? <Typography sx={{ fontSize: 13, color: NIGHT.dim }}>{subtitle}</Typography> : null}
        </Box>
        {/* ml auto keeps the action right-aligned even when the header wraps
            onto its own line on mobile */}
        {action ? <Box sx={{ flexShrink: 0, ml: 'auto' }}>{action}</Box> : null}
      </Box>
      {children ? <Box sx={{ mt: 1.5 }}>{children}</Box> : null}
    </Box>
  );
}
NightSection.propTypes = {
  art: PropTypes.string,
  artSize: PropTypes.number,
  artGlow: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.node,
  action: PropTypes.node,
  children: PropTypes.node,
  sx: PropTypes.object
};

// Dark inner row/tile used inside NightSection lists.
export const nightTileSx = {
  borderRadius: 2.5,
  px: 2,
  py: 1.25,
  bgcolor: 'rgba(16,11,30,0.72)',
  border: '1px solid rgba(167,139,250,0.28)'
};

// Dark-theme TextField overrides for inputs living on NightSection cards.
// The label backgrounds carry !important: the app theme masks the outline
// notch with a solid paper-white label background, and in the production
// bundle the theme's styleOverrides win the emotion insertion-order race
// against plain sx (the white patch shipped once — 2026-07-06).
export const nightFieldSx = {
  '& .MuiInputBase-input': { color: NIGHT.white },
  '& .MuiInputBase-input::placeholder': { color: NIGHT.dim, opacity: 1 },
  '& .MuiInputBase-root': { bgcolor: 'rgba(16,11,30,0.6)' },
  '& .MuiInputLabel-root': { color: `${NIGHT.dim} !important`, background: 'transparent !important' },
  '& .MuiInputLabel-shrink': { background: '#151026 !important', px: 0.5, borderRadius: 1 },
  '& .MuiInputLabel-root.Mui-focused': { color: `${NIGHT.violetSoft} !important` },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(167,139,250,0.35)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(167,139,250,0.6)' },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: `${NIGHT.violetSoft} !important` }
};

// Shared control metrics so gold + outlined actions line up with the 40px
// small TextFields no matter which card they sit on.
const actionMetrics = { height: 40, px: 2.5, whiteSpace: 'nowrap', flexShrink: 0 };

// Outlined button that fits the night cards.
export const nightButtonSx = {
  ...actionMetrics,
  fontWeight: 700,
  borderRadius: 99,
  color: NIGHT.violetSoft,
  border: '1px solid rgba(167,139,250,0.45)',
  '&:hover': { border: '1px solid rgba(167,139,250,0.75)', bgcolor: 'rgba(139,92,246,0.12)' },
  '&.Mui-disabled': { color: 'rgba(245,243,255,0.35)', border: '1px solid rgba(167,139,250,0.2)' }
};

// Filled gold action button (small form of GoldCTA for inline actions).
// Disabled = quiet outline, never a muddy gold fill.
export const goldButtonSx = {
  ...actionMetrics,
  fontWeight: 800,
  borderRadius: 99,
  color: '#1d1503',
  background: `linear-gradient(180deg, ${NIGHT.goldHi} 0%, ${NIGHT.gold} 55%, #caa028 100%)`,
  boxShadow: '0 0 16px rgba(245,198,68,0.3), inset 0 1px 2px rgba(255,255,255,0.7)',
  '&:hover': { background: `linear-gradient(180deg, #fff3c4 0%, ${NIGHT.gold} 55%, #caa028 100%)` },
  '&.Mui-disabled': {
    color: 'rgba(245,243,255,0.35)',
    background: 'transparent',
    border: '1px solid rgba(245,198,68,0.25)',
    boxShadow: 'none'
  }
};

// ---- warm-up + mercy (owner 2026-07-09): first-tap safety for the skill
// games. Every skill play opens with a FREE practice round (skippable) so a
// scarce earned credit is never burned learning the controls; a blank real
// run gets the server's one-time mercy retry. Shared across all three games.

// Cyan "doesn't count" badge shown in the TopBar during the practice round.
export function WarmupPill({ sx }) {
  return (
    <StatusPill accent="rgba(34,211,238,0.6)" sx={sx}>
      <Typography sx={{ fontWeight: 900, fontSize: 12, letterSpacing: '0.2em', textIndent: '0.2em', color: NIGHT.cyan }}>
        WARM-UP
      </Typography>
    </StatusPill>
  );
}
WarmupPill.propTypes = { sx: PropTypes.object };

// Dim secondary link under the intro CTA — skipping practice is the explicit
// action; the big gold button (the one nobody reads before tapping) always
// leads somewhere safe.
export function SkipWarmupLink({ onSkip }) {
  return (
    <ButtonBase
      onClick={onSkip}
      sx={{
        mt: 0.5,
        px: 2,
        py: 0.75,
        borderRadius: 99,
        fontSize: 13,
        fontFamily: 'inherit',
        color: NIGHT.dim,
        textDecoration: 'underline',
        textUnderlineOffset: 3,
        '&:hover': { color: NIGHT.white }
      }}
    >
      Skip the warm-up, straight to the real run
    </ButtonBase>
  );
}
SkipWarmupLink.propTypes = { onSkip: PropTypes.func.isRequired };

// Full-screen beat between the practice round and the run that counts.
export function WarmupHandoff({ onStart, line }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        px: 3,
        textAlign: 'center',
        background: 'rgba(6,4,14,0.78)',
        backdropFilter: 'blur(8px)',
        animation: 'handoffFade 0.35s ease both',
        '@keyframes handoffFade': { from: { opacity: 0 }, to: { opacity: 1 } }
      }}
    >
      <Headline kicker="WARM-UP OVER" title="READY?" />
      <Typography sx={{ fontSize: 16, color: NIGHT.dim, maxWidth: 340 }}>
        {line || 'That round was just practice, nothing counted.'} The next run is the real one.
      </Typography>
      <Box
        sx={{
          mt: 1.5,
          animation: 'readyPulse 1.6s ease-in-out infinite',
          '@keyframes readyPulse': { '0%, 100%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.04)' } }
        }}
      >
        <GoldCTA onClick={onStart}>START THE REAL RUN</GoldCTA>
      </Box>
    </Box>
  );
}
WarmupHandoff.propTypes = { onStart: PropTypes.func.isRequired, line: PropTypes.string };

// Full-screen mercy beat: the server declined to consume the credit for a
// blank first run (once per game, ever) — offer the do-over or the exit.
export function MercyCard({ onRetry, onLater }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        px: 3,
        textAlign: 'center',
        background: 'rgba(6,4,14,0.78)',
        backdropFilter: 'blur(8px)',
        animation: 'mercyFade 0.35s ease both',
        '@keyframes mercyFade': { from: { opacity: 0 }, to: { opacity: 1 } }
      }}
    >
      <Headline kicker="THAT ONE'S ON US" title="GO AGAIN" />
      <Typography sx={{ fontSize: 16, color: NIGHT.dim, maxWidth: 340 }}>
        Over too fast to count. Your play is still yours — take the one-time do-over.
      </Typography>
      <Box
        sx={{
          mt: 1.5,
          animation: 'readyPulse 1.6s ease-in-out infinite',
          '@keyframes readyPulse': { '0%, 100%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.04)' } }
        }}
      >
        <GoldCTA onClick={onRetry}>TRY AGAIN</GoldCTA>
      </Box>
      <ButtonBase
        onClick={onLater}
        sx={{
          px: 2,
          py: 0.75,
          borderRadius: 99,
          fontSize: 13,
          fontFamily: 'inherit',
          color: NIGHT.dim,
          textDecoration: 'underline',
          textUnderlineOffset: 3,
          '&:hover': { color: NIGHT.white }
        }}
      >
        Save my play for later
      </ButtonBase>
    </Box>
  );
}
MercyCard.propTypes = { onRetry: PropTypes.func.isRequired, onLater: PropTypes.func.isRequired };

// ---- capsule progress bar (reference: violet→cyan fill, glowing tip) ----
export function NeonProgress({ value, sx }) {
  const v = Math.max(0, Math.min(100, value));
  return (
    <Box
      sx={{
        position: 'relative',
        height: 18,
        borderRadius: 99,
        bgcolor: 'rgba(20,14,36,0.85)',
        border: '1px solid rgba(167,139,250,0.4)',
        overflow: 'hidden',
        ...sx
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          width: `${v}%`,
          borderRadius: 99,
          background: `linear-gradient(90deg, ${NIGHT.violet} 0%, ${NIGHT.cyan} 100%)`,
          boxShadow: '0 0 16px rgba(34,211,238,0.55)',
          transition: 'none'
        }}
      />
      {v > 3 && v < 100 ? (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: `${v}%`,
            width: 10,
            height: 10,
            borderRadius: '50%',
            transform: 'translate(-6px, -50%)',
            bgcolor: '#fff',
            boxShadow: '0 0 14px #fff, 0 0 26px rgba(34,211,238,0.9)'
          }}
        />
      ) : null}
    </Box>
  );
}
NeonProgress.propTypes = { value: PropTypes.number.isRequired, sx: PropTypes.object };
